import { type PropsWithChildren } from "react";
import { SidebarProvider } from "ui";

import { ChatContextProvider } from "src/chat/ChatContext";
import { CommentsRealtimeProvider } from "src/comments/CommentsRealtimeProvider";
import { ScopedRepositoryProviders } from "src/components/ScopedRepositoryProviders";
import { BreadcrumbsProvider } from "src/contexts/BreadcrumbsContext";
import { RouteOptionsProvider } from "src/contexts/RouteOptionsContext";
import { EventBusProvider } from "src/events";
import { usePreference } from "src/hooks/usePreference";
import { NotificationsRealtimeProvider } from "src/notifications/NotificationsRealtimeProvider";

import { AuthenticatedNavigation } from "./AuthenticatedNavigation";
import { AuthenticatedNavigationContextProvider } from "./AuthenticatedNavigationContext";
import { Sidebar } from "./Sidebar";

interface Props extends PropsWithChildren {
  navigationTitle?: string;
  showSidebar?: boolean;
}

export function AuthLayout(props: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = usePreference("sidebarOpen");

  return (
    <EventBusProvider>
      <CommentsRealtimeProvider>
        <NotificationsRealtimeProvider>
          <ChatContextProvider>
            <ScopedRepositoryProviders>
              <SidebarProvider
                open={isSidebarOpen}
                onOpenChange={setIsSidebarOpen}
                className="fixed inset-0 flex overflow-hidden"
              >
                <RouteOptionsProvider>
                  <BreadcrumbsProvider>
                    {props.showSidebar === false ? null : <Sidebar />}
                    <AuthenticatedNavigationContextProvider>
                      <AuthenticatedNavigation
                        sidebarShown={props.showSidebar}
                        title={props.navigationTitle}
                      />
                      <main
                        data-scroll-container
                        className="max-h-full flex flex-1 flex-col pt-sztama-auth-navigation-height overflow-x-hidden"
                      >
                        {props.children}
                      </main>
                    </AuthenticatedNavigationContextProvider>
                  </BreadcrumbsProvider>
                </RouteOptionsProvider>
              </SidebarProvider>
            </ScopedRepositoryProviders>
          </ChatContextProvider>
        </NotificationsRealtimeProvider>
      </CommentsRealtimeProvider>
    </EventBusProvider>
  );
}
